import { Link } from "react-router-dom";
import type { Article } from "@/types/content";

function formatHeat(heat: number) {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)} 万`;
  return `${heat}`;
}

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "wide" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50"
      >
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md">
          <img
            src={article.image}
            alt={`${article.title} 配图`}
            title={`${article.title} - 热门话题配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0">
          <span className="text-xs font-medium text-secondary-600">{article.category}</span>
          <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
            {article.title}
          </h3>
          <p className="mt-1 text-xs text-foreground-500">{formatHeat(article.heat)} 热度</p>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid grid-cols-1 gap-5 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:grid-cols-2"
      >
        <div className="h-48 w-full overflow-hidden rounded-md md:h-56">
          <img
            src={article.image}
            alt={`${article.title} 配图`}
            title={`${article.title} - 热门话题配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.date}</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-xl">
            {article.title}
          </h3>
          <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-fire-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              {formatHeat(article.heat)} 热度
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-message-3-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              {article.comments} 条讨论
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="h-48 w-full overflow-hidden">
        <img
          src={article.image}
          alt={`${article.title} 配图`}
          title={`${article.title} - 热门话题配图`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
            {article.category}
          </span>
          <span className="text-xs text-foreground-500">{article.date}</span>
        </div>
        <h3 className="mt-3 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-fire-line w-4 h-4 flex items-center justify-center"></i>
            </span>
            {formatHeat(article.heat)}
          </span>
          <span>{article.readTime}阅读</span>
        </div>
      </div>
    </Link>
  );
}